import React, { Fragment, useState, useReducer, useMemo } from 'react'
import { Link as RouterLink } from "react-router-dom"
import { createForm } from 'rc-form'
import { ZipCodeTW } from "zipcode-tw-react";
import { useTranslation } from 'react-i18next'
import { fromJS } from 'immutable';
import cn from 'classnames'
import dayjs from 'dayjs'
import numeral from 'numeral'
import DatePicker, { registerLocale } from 'react-datepicker';
import { addDays, getHours } from 'date-fns'
import zhTW from 'date-fns/locale/zh-TW'

import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import TextField from '@material-ui/core/TextField'
import Edit from '@material-ui/icons/Edit'
import Link from '@material-ui/core/Link'
import Popover from '@material-ui/core/Popover';

import Block from 'components/common/Block'
import UpdateInvoiceDialog from 'components/dialog/UpdateInvoiceDialog'
import AddCreditCardDialog from 'components/dialog/AddCreditCardDialog'

import "react-datepicker/dist/react-datepicker.css";

import {
    formatNumber, formatDate,
    receiptTypeEnum, shippingTypeEnum, shippingUrgencyEnum,
    shippingTimeSessionEnum, isObjectEmpty,
    couponTypeEnum,
    calcDiscountPrice, convertNegativeToZero,
} from 'libraries/utils'

registerLocale('zh-TW', zhTW)

const TODAY = new Date();
const TOMORROW = addDays(TODAY, 1)
const THE_DAY_AFTER_TOMORROW = addDays(TODAY, 2);

function setDefaultValue(defaultValues, key, value) {
    if (!value) return defaultValues
    return {
        ...defaultValues,
        [key]: value,
    }
}

function init(props) {
    const { shippingOrderData, memberInfo, couponlist } = props
    const formatAddress = fromJS(memberInfo.formatAddress || {})
    const initValues = {
        shippingType: shippingTypeEnum.HOME, // 取貨方式

        city: null, // 縣市
        area: null, // 區
        zipCode: null, // 郵遞區號

        receiptType: receiptTypeEnum.THREE_LINE, // 發票種類
        receiptTitle: '', // 發票抬頭
        receiptTaxId: '', // 發票 統一編號

        ccLastFour: '', // 信用卡後四碼

        shippingUrgency: shippingUrgencyEnum.ORDINARY, // 急件總類
        shippingDate: THE_DAY_AFTER_TOMORROW, //取貨日期
        shippingTimeSession: shippingTimeSessionEnum.NOT_SPECIFY, // 指定時間

        couponId: null, // 優惠卷 id
        selectedCoupon: fromJS({}),
    }

    if (shippingOrderData.size > 0) {
        let defaultValues = {
            shippingType: shippingOrderData.get('shippingType'),
            city: shippingOrderData.get('city'),
            area: shippingOrderData.get('area'),
            zipCode: shippingOrderData.get('zipCode'),
            receiptType: shippingOrderData.get('receiptType').toString(),
            receiptTitle: initValues.receiptTitle,
            receiptTaxId: initValues.receiptTaxId,
            ccLastFour: shippingOrderData.get('ccLastFour'),
            shippingUrgency: shippingOrderData.get('shippingUrgency').toString(),
            couponId: shippingOrderData.get('couponId'),
            selectedCoupon: shippingOrderData.get('selectedCoupon'),
        }

        if (defaultValues.receiptType === receiptTypeEnum.THREE_LINE) {
            defaultValues.receiptTitle = shippingOrderData.get('receiptTitle')
            defaultValues.receiptTaxId = shippingOrderData.get('receiptTaxId')
        }

        const shippingTime = shippingOrderData.get('shippingTime').split(' ')

        defaultValues.shippingDate = new Date(shippingTime[0])
        defaultValues.shippingTimeSession = shippingTime[1]

        return defaultValues

    } else {
        let defaultValues = { ...initValues }
        if (formatAddress.size > 0) {
            defaultValues = setDefaultValue(defaultValues, 'city', formatAddress.get('city'))
            defaultValues = setDefaultValue(defaultValues, 'area', formatAddress.get('area'))
            defaultValues = setDefaultValue(defaultValues, 'zipCode', formatAddress.get('zipCode'))
        }

        defaultValues = setDefaultValue(defaultValues, 'receiptType', memberInfo.receiptType.toString())
        defaultValues = setDefaultValue(defaultValues, 'receiptTitle', memberInfo.receiptTitle)
        defaultValues = setDefaultValue(defaultValues, 'receiptTaxId', memberInfo.receiptTaxId)
        defaultValues = setDefaultValue(defaultValues, 'ccLastFour', memberInfo.creditCardLastFour)

        return defaultValues
    }
}

function formDataReducer(state, action) {
    switch (action.type) {
        case 'update_shipping_type':
            return {
                ...state,
                shippingType: action.shippingType,
            }
        case 'update_address':
            return {
                ...state,
                city: action.city,
                area: action.area,
                zipCode: action.zipCode,
            }
        case 'update_receipt_type':
            return {
                ...state,
                receiptType: action.receiptType,
            }
        case 'update_receipt_info':
            return {
                ...state,
                receiptType: action.receiptType,
                receiptTitle: action.receiptTitle,
                receiptTaxId: action.receiptTaxId,
            }
        case 'update_credit_card':
            return {
                ...state,
                ccLastFour: action.ccLastFour,
            }
        case 'update_coupon':
            return {
                ...state,
                selectedCoupon: action.selectedCoupon,
                couponId: action.couponId,
            }
        case 'update_date':
            return {
                ...state,
                shippingDate: action.date,
            }
        case 'update_time':
            return {
                ...state,
                shippingTimeSession: action.time,
            }
        case 'update_urgency':
            const { urgent } = action
            let shippingDate = THE_DAY_AFTER_TOMORROW
            let shippingTimeSession = shippingTimeSessionEnum.NOT_SPECIFY

            if (urgent === shippingUrgencyEnum.TODAY) {
                shippingDate = TODAY
                shippingTimeSession = shippingTimeSessionEnum.NIGHT

            } else if (urgent === shippingUrgencyEnum.NEXT_DAY) {
                shippingDate = TOMORROW
                shippingTimeSession = shippingTimeSessionEnum.NIGHT
            }

            return {
                ...state,
                shippingUrgency: action.urgent,
                shippingDate,
                shippingTimeSession,
            }
        default:
            return state
    }
}

const FormFiedGrid = props => (
    <Grid container alignItems={props.alignItems || "center"}
        className={cn('form-field', props.className)} style={{ ...props.style }}
    >
        <Grid item xs={3}>
            <Typography className="text-grey">{props.title}</Typography>
        </Grid>
        <Grid item xs={9}>
            {props.children}
        </Grid>
    </Grid>
)

const ShoppingCartForm = props => {
    const { t } = useTranslation();
    const {
        memberInfo, historyAddress, serviceFee, shippingFee, amountWineInCart,
        couponlist, shippingOrderData,
    } = props
    const { getFieldProps, getFieldError, setFieldsValue, validateFields } = props.form
    const formatAddress = fromJS(memberInfo.formatAddress || {})

    // form field
    const [formData, dispatch] = useReducer(formDataReducer, props, init)
    const {
        shippingType,
        city,
        area,
        zipCode,
        receiptType,
        receiptTitle,
        receiptTaxId,
        ccLastFour,
        shippingUrgency, shippingDate, shippingTimeSession,
        couponId,
        selectedCoupon,
    } = formData

    const [anchorEl, setAnchorEl] = React.useState(null) // 從歷史訂單選擇地址

    const isUrgent = shippingUrgency === shippingUrgencyEnum.TODAY || shippingUrgency === shippingUrgencyEnum.NEXT_DAY;
    const isDisabledToday = getHours(new Date) > 10 // 當日上午11點過後無法勾選“當日特急件”

    // error
    const [shippingErr, setShippingErr] = useState(false)
    const [receiptErr, setReceiptErr] = useState(false)
    const [creditCardErr, setCreditCardErr] = useState(false)

    // dialog
    const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false)
    const [openCreditCardDialog, setOpenCreditCardDialog] = useState(false)

    const popoverOpen = Boolean(anchorEl)
    const popoverId = popoverOpen ? 'simple-popover' : undefined;

    // 服務費用
    const servise_fee_unit = serviceFee.get(shippingUrgency.toString()) || 0
    const servise_fee = numeral(amountWineInCart).multiply(servise_fee_unit)

    // 物流費用
    const shipping_fee = useMemo(() => {
        if (shippingType === shippingTypeEnum.STORE_PICK_UP) return 0 // 自取 物流費用 = 0
        return shippingFee.get(amountWineInCart.toString()) || 0
    }, [shippingType, shippingFee, amountWineInCart])

    // 小計
    const subTotal = numeral(servise_fee).add(shipping_fee)

    // 總計
    let couponAmount = selectedCoupon.get('couponAmount') || 0
    let couponType = selectedCoupon.get('couponType')

    let discountServiseFee = calcDiscountPrice(servise_fee, couponType === couponTypeEnum.SERVICEFEE ? couponAmount : 0)
    let discountShippingFee = calcDiscountPrice(shipping_fee, couponType === couponTypeEnum.SHIPPINGFEE ? couponAmount : 0)

    let total = numeral(discountServiseFee).add(discountShippingFee)

    // issue #21: if total is negative, show zero and not pay.
    if (total.value() < 0) {
        total = total.set(0)
    }

    const handleInvoiceDialog = () => {
        setOpenInvoiceDialog(!openInvoiceDialog)
    }

    const handleHistoryAdsressClick = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const handleCreditCardDialog = () => {
        setOpenCreditCardDialog(!openCreditCardDialog)
    }

    const handlePopoverClose = () => {
        setAnchorEl(null)
    }

    const handleShippingType = (event) => {
        dispatch({
            type: 'update_shipping_type',
            shippingType: event.target.value,
        })
    }

    const handleChangeZipCode = ({
        countyValue,
        districtValue,
        zipValue
    }) => {
        dispatch({
            type: 'update_address',
            city: countyValue,
            area: districtValue,
            zipCode: zipValue,
        })
    }

    const handleReceiptType = (event) => {
        dispatch({
            type: 'update_receipt_type',
            receiptType: event.target.value,
        })
    }

    const udpateCreditCard = (data) => {
        dispatch({
            type: 'update_credit_card',
            ccLastFour: data
        })
        setOpenCreditCardDialog(false)
    }

    const updateInvoice = (data) => {
        dispatch({
            type: 'update_receipt_info',
            receiptType: data.receiptType.toString(),
            receiptTitle: data.receiptTitle,
            receiptTaxId: data.receiptTaxId,
        })
        setOpenInvoiceDialog(false)
    }

    const onClickAdress = (event) => {
        const addressData = event.target.dataset
        dispatch({
            type: 'update_address',
            city: addressData.city,
            area: addressData.area,
            zipCode: addressData.zipCode,
        })

        setFieldsValue({
            address: addressData.address,
        })
        setAnchorEl(null)
    }

    const handleShippingUrgency = (event) => {
        dispatch({
            type: 'update_urgency',
            urgent: event.target.value,
        })
    }

    const handleShippingDate = (date) => {
        dispatch({
            type: 'update_date',
            date,
        })
    }

    const handleShippingTimeSession = (event) => {
        dispatch({
            type: 'update_time',
            time: event.target.value,
        })
    }

    const handleCouponChange = (event) => {
        const couponId = event.target.value
        const selectedCoupon = couponlist.filter(item => item.get('storageCouponId') === couponId).get(0)
            || fromJS({})
        dispatch({
            type: 'update_coupon',
            selectedCoupon: selectedCoupon,
            couponId: couponId,
        })
    }

    const handleSubmit = () => {
        setShippingErr(false)
        setReceiptErr(false)
        setCreditCardErr(false)
        validateFields((error, value) => {
            const allErr = Object.assign({}, error)
            if (shippingType === shippingTypeEnum.STORE_PICK_UP && (allErr.name || allErr.phone || allErr.shippingTime)) {
                console.warn(`shippingType=${shippingType} has invalid shipping form`);
                console.warn(`allErr.name=${allErr.name}, allErr.phone=${allErr.phone}, allErr.shippingTime=${allErr.shippingTime}`);
                setShippingErr(true);
                allErr.shipping = true;
            } else if (shippingType === shippingTypeEnum.HOME && (allErr.name || allErr.phone || allErr.address || allErr.shippingTime || !city || !area || !zipCode)) {
                console.warn(`shippingType=${shippingType} has invalid shipping form`);
                console.warn(`allErr.name=${allErr.name}, allErr.phone=${allErr.phone}, allErr.shippingTime=${allErr.shippingTime}`);
                console.warn(`city=${city}, area=${area}, zipCode=${zipCode}`);
                setShippingErr(true)
                allErr.shipping = true
            }
            if (receiptType === '3' && (!receiptTitle || !receiptTaxId)) {
                setReceiptErr(true)
                allErr.receipt = true
            }
            if (!ccLastFour) {
                setCreditCardErr(true)
                allErr.creditcard = true
            }
            if (isObjectEmpty(allErr)) {
                const formData = {
                    ...value,
                    shippingType,
                    city,
                    area,
                    zipCode,
                    shippingTime: `${dayjs(shippingDate).format('YYYY-MM-DD')} ${shippingTimeSession}`, //取貨日期 + 時段
                    shippingUrgency,
                    receiptType: parseInt(receiptType),
                    receiptTitle,
                    receiptTaxId,
                    couponId,
                    selectedCoupon,
                    ccLastFour,
                    total: total.value()
                }
                props.onSubmit(formData)
            }
        })
    }

    return (
        <Fragment>
            {/* 物流資訊 */}
            <Block
                title={t('shipping_info')}
                contClassname="bg-white main-block"
                error={shippingErr}
                errorMsg={'*物流資料不完整或有誤'}
            >
                <FormFiedGrid title={t('shipping_type')}>
                    <Select
                        value={shippingType}
                        variant="filled"
                        onChange={handleShippingType}
                        input={<OutlinedInput labelWidth={0} margin="dense" fullWidth />}
                    >
                        <MenuItem value={shippingTypeEnum.HOME}>
                            {t('home_delivery')}
                        </MenuItem>
                        <MenuItem value={shippingTypeEnum.STORE_PICK_UP}>
                            {t('store_pick_up')}
                        </MenuItem>
                    </Select>
                </FormFiedGrid>
                <FormFiedGrid title={t('picker_name')}>
                    <TextField
                        name="name"
                        margin="dense"
                        variant="outlined"
                        style={{ margin: 0 }}
                        fullWidth
                        inputProps={{
                            ...getFieldProps('name', {
                                initialValue: memberInfo.realName || '',
                                rules: [{
                                    required: true,
                                    type: 'string',
                                }]
                            })
                        }}
                    />
                </FormFiedGrid>
                <FormFiedGrid title={t('picker_tel')}>
                    <TextField
                        name="phone"
                        margin="dense"
                        variant="outlined"
                        style={{ margin: 0 }}
                        fullWidth
                        inputProps={{
                            ...getFieldProps('phone', {
                                initialValue: memberInfo.phone || '',
                                onChange(e) {
                                    e.target.value = e.target.value.trim()
                                },
                                rules: [{
                                    required: true,
                                    type: 'string',
                                    pattern: /^[0-9]*$/,
                                }]
                            })
                        }}
                    />
                </FormFiedGrid>
                <FormFiedGrid title={t('shipping_address')}
                    alignItems="flex-start" className="address"
                >
                    {shippingType === shippingTypeEnum.HOME ?
                        <div>
                            <ZipCodeTW
                                displayType="text"
                                countyClass="zip-code"
                                districtClass="zip-code"
                                zipStyle={{ display: 'none' }}
                                countyValue={city}
                                districtValue={area}
                                handleChangeCounty={handleChangeZipCode}
                                handleChangeDistrict={handleChangeZipCode}
                            />
                            <TextField
                                name="address"
                                margin="dense"
                                variant="outlined"
                                style={{ margin: '15px 0 0' }}
                                inputProps={{
                                    ...getFieldProps('address', {
                                        initialValue: formatAddress.get('address') || '',
                                        rules: [{
                                            required: true,
                                            type: 'string',
                                        }]
                                    }),
                                }}
                                fullWidth
                            />
                            <Typography
                                aria-describedby={popoverId}
                                onClick={handleHistoryAdsressClick}
                                className="pointer"
                                color="primary"
                                variant="body2"
                                style={{ marginTop: '10px' }}
                            >
                                從歷史訂單選擇
                            </Typography>
                            <Popover
                                id={popoverId}
                                open={popoverOpen}
                                anchorEl={anchorEl}
                                onClose={handlePopoverClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                }}
                                transformOrigin={{
                                    vertical: 'center',
                                    horizontal: 'center',
                                }}
                            >
                                {historyAddress.size ?
                                    historyAddress.map((address, idx) => (
                                        <MenuItem
                                            key={`address-${idx}`}
                                            onClick={onClickAdress}
                                            data-city={address.get('city')}
                                            data-area={address.get('area')}
                                            data-address={address.get('address')}
                                            data-zipCode={address.get('zipCode')}
                                        >
                                            {address.get('city')}{address.get('area')}{address.get('address')}
                                        </MenuItem>
                                    ))
                                    :
                                    <MenuItem>{t('no_data')}</MenuItem>
                                }
                            </Popover>
                        </div>
                        :
                        <div>
                            <Typography>
                                CellWine實體酒窖
                            </Typography>
                            <Typography className="text-grey"
                                style={{ fontWeight: 500, marginTop: '5px' }}
                            >
                                台北市大安區新生南路二段2號B1 <br />
                                02-23222885 <br />
                                10:00~18:00 (Mon~ Fri)
                            </Typography>
                        </div>
                    }
                </FormFiedGrid>
                <FormFiedGrid title={t('pickup_date')} alignItems="flex-start" className="shipping-date">
                    <RadioGroup
                        name="shippingUrgency"
                        value={shippingUrgency}
                        onChange={handleShippingUrgency}
                    >
                        <FormControlLabel
                            value={shippingUrgencyEnum.TODAY}
                            label={t('today_urgent')}
                            control={<Radio color="primary" />}
                            disabled={isDisabledToday}
                        />
                        <FormControlLabel
                            value={shippingUrgencyEnum.NEXT_DAY}
                            label={t('next_day_urgent')}
                            control={<Radio color="primary" />}
                        />
                        <FormControlLabel
                            value={shippingUrgencyEnum.ORDINARY}
                            label={t('ordinary')}
                            control={<Radio color="primary" />}
                        />
                    </RadioGroup>
                    <DatePicker
                        selected={shippingDate}
                        onChange={handleShippingDate}
                        className="date-picker-green MuiTypography-root MuiTypography-body1"
                        locale="zh-TW"
                        disabled={isUrgent}
                        minDate={THE_DAY_AFTER_TOMORROW}
                        placeholderText={'abc'}
                    />
                    <Link
                        className="shipping-date-tip"
                        to="/price-detail"
                        component={RouterLink}
                        underline="none"
                        color="primary"
                        target='_blank'
                    >
                        {t('urgent_tip')}
                    </Link>
                </FormFiedGrid>
                <FormFiedGrid title={t('pickup_time')}>
                    <Select
                        value={shippingTimeSession}
                        variant="filled"
                        onChange={handleShippingTimeSession}
                        input={
                            <OutlinedInput
                                labelWidth={0}
                                margin="dense"
                                fullWidth
                            />
                        }
                    >
                        {!isUrgent &&
                            <MenuItem value={shippingTimeSessionEnum.NOT_SPECIFY}>
                                {t('arrival_time_not_specify')}
                            </MenuItem>
                        }
                        {!isUrgent &&
                            <MenuItem value={shippingTimeSessionEnum.MORNING}>
                                {t('arrival_time_morning')}
                            </MenuItem>
                        }
                        {!isUrgent &&
                            <MenuItem value={shippingTimeSessionEnum.AFTERNOON}>
                                {t('arrival_time_afternoon')}
                            </MenuItem>
                        }
                        {isUrgent &&
                            <MenuItem value={shippingTimeSessionEnum.NIGHT}>
                                {t('arrival_time_night')}
                            </MenuItem>
                        }
                    </Select>
                </FormFiedGrid>
                <FormFiedGrid title={t('remark')} alignItems="flex-start">
                    <TextField
                        margin="dense"
                        name="remark"
                        variant="outlined"
                        style={{ margin: 0 }}
                        inputProps={{
                            ...getFieldProps('message', {
                            })
                        }}
                        multiline
                        rows={5}
                        fullWidth
                    />
                </FormFiedGrid>
            </Block>

            {/* 發票資訊 */}
            <Block
                title={t('receipt_info')}
                contClassname="bg-white main-block"
                error={receiptErr}
                errorMsg={'*發票資料不完整或有誤'}
            >
                <RadioGroup
                    name="receiptType"
                    value={receiptType}
                    onChange={handleReceiptType}
                    className="receipt-type"
                >
                    <FormControlLabel value={receiptTypeEnum.TWO_LINE} label={receiptTypeEnum['2']}
                        control={<Radio color="primary" />}
                    />
                    <FormControlLabel value={receiptTypeEnum.THREE_LINE} label={receiptTypeEnum['3']}
                        control={<Radio color="primary" />}
                    />
                </RadioGroup>
                <Typography className="text-grey">＊{t('receipt_info_tip')}</Typography>
                {receiptType === receiptTypeEnum.THREE_LINE &&
                    <Grid container style={{ marginTop: '20px' }}>
                        <Grid container>
                            <Typography>{t('receipt_info')}</Typography>
                            <Edit
                                color="primary"
                                className="pointer"
                                onClick={handleInvoiceDialog}
                                style={{ marginLeft: '10px' }}
                            />
                        </Grid>
                        <Grid container>
                            <Typography className="text-grey">{t('receipt_title')}：{receiptTitle}</Typography>
                        </Grid>
                        <Grid container>
                            <Typography className="text-grey">{t('tax_id')}：{receiptTaxId}</Typography>
                        </Grid>
                    </Grid>
                }
            </Block>

            {/* 費用統計 */}
            <Block
                title={t('fee_info')}
                contClassname="bg-white main-block"
                error={creditCardErr}
                errorMsg={t('credit_card_error_msg')}
            >
                <FormFiedGrid title={t('payment_method')}>
                    <Typography>
                        {ccLastFour ?
                            `${t('credit_card')} ···· ···· ···· ${ccLastFour}`
                            :
                            t('add_credit_card_1')
                        }
                        <Edit
                            color="primary"
                            className="pointer"
                            style={{ marginLeft: '5px' }}
                            fontSize={'small'}
                            onClick={handleCreditCardDialog}
                        />
                    </Typography>
                </FormFiedGrid>
                <FormFiedGrid title={t('shipping_detail')}>
                    <Typography>
                        {amountWineInCart} {t('bottle')}
                    </Typography>
                </FormFiedGrid>
                <FormFiedGrid title={t('service_fee')}>
                    <Typography>
                        {formatNumber(servise_fee)} {t('currency_unit')} {`(${shippingUrgencyEnum[shippingUrgency]})`}
                        <Link
                            to="/price-detail"
                            component={RouterLink}
                            underline="none"
                            color="primary"
                            target='_blank'
                            style={{ paddingLeft: '15px' }}
                        >
                            {t('service_fee_tip')}
                        </Link>
                    </Typography>
                </FormFiedGrid>
                <FormFiedGrid title={t('logistics_cost')}>
                    <Typography>
                        {formatNumber(shipping_fee)} {t('currency_unit')}
                        <Link
                            to="/price-detail"
                            component={RouterLink}
                            underline="none"
                            color="primary"
                            style={{ paddingLeft: '15px' }}
                            target='_blank'
                        >
                            {t('logistics_cost_tip')}
                        </Link>
                    </Typography>
                </FormFiedGrid>
                <FormFiedGrid title={t('total')}
                    style={{
                        borderTop: '2px solid rgba(74, 79, 85, 0.1)',
                        paddingTop: '20px'
                    }}
                >
                    <Typography>
                        {formatNumber(subTotal)} {t('currency_unit')}
                    </Typography>
                </FormFiedGrid>
                <FormFiedGrid title={t('coupon')}>
                    <Select
                        value={selectedCoupon.get('storageCouponId') || 0}
                        variant="filled"
                        onChange={handleCouponChange}
                        autoWidth={true}
                        input={
                            <OutlinedInput
                                labelWidth={0}
                                margin="dense"
                                fullWidth
                            />
                        }
                        renderValue={(value) => (
                            <span>{selectedCoupon.get('name') || t('not_use_coupon')}</span>
                        )}
                    >
                        <MenuItem value={0}
                            style={{
                                borderBottom: '1px solid rgba(74, 79, 85, 0.1)',
                                textAlign: 'center',
                                width: '500px'
                            }}
                        >
                            {t('not_use_coupon')}
                        </MenuItem>
                        {couponlist.map((coupon, idx) => (
                            <MenuItem key={idx} value={coupon.get('storageCouponId')}>
                                <Grid container>
                                    <Grid item xs={10}>
                                        {coupon.get('name')}<br />
                                        <Typography className="text-grey">
                                            {coupon.get('description')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={2} style={{ color: '#e74c39', fontWeight: 500 }}>
                                        {t('coupon_expiry_date')}：<br />
                                        {formatDate(coupon.get('expiredAt'))}
                                    </Grid>
                                </Grid>
                            </MenuItem>
                        ))}
                    </Select>
                </FormFiedGrid>
                <FormFiedGrid title={t('coupon_bonus')} className="bonus">
                    {!!selectedCoupon.size &&
                        <Typography>
                            -{formatNumber(selectedCoupon.get('couponAmount'))} {t('currency_unit')}
                        </Typography>
                    }
                </FormFiedGrid>
                <Grid container style={{ marginTop: '28px' }}>
                    <Grid item xs={2}>
                        <Typography className="text-grey">{t('total_fee')}</Typography>
                    </Grid>
                    <Grid item xs={10}>
                        <Typography color="primary" variant="h1">
                            {formatNumber(total)}
                            <span style={{ fontSize: '16px', color: '#4a4f55', paddingLeft: '5px' }}>
                                {t('currency_unit')}
                            </span>
                        </Typography>
                    </Grid>
                </Grid>
            </Block>
            <Button variant="contained" color="primary" fullWidth
                disabled={!amountWineInCart || amountWineInCart > 24}
                onClick={handleSubmit}
            >
                {t('next_step_confirm')}
            </Button>
            <UpdateInvoiceDialog
                open={openInvoiceDialog}
                onClose={handleInvoiceDialog}
                onSubmit={updateInvoice}
            />
            <AddCreditCardDialog
                open={openCreditCardDialog}
                onClose={handleCreditCardDialog}
                onSucc={udpateCreditCard}
            />
        </Fragment>
    )
}

export default createForm()(ShoppingCartForm)